import { Router, Routes } from '@angular/router';
import { PublicLayoutComponent } from './layouts/public-layout/public-layout.component';
import { PostAuthLayoutComponent } from './layouts/post-auth-layout/post-auth-layout.component';
import { HomeLayoutComponent } from './layouts/post-auth-layout/home-layout/homeLayout.component';
import { MainlayoutComponent } from './layouts/post-auth-layout/mainlayout/mainlayout.component';
import { BlankLayoutComponent } from './layouts/blank-layout/blank-layout.component';
import { authGuard } from './_guards/auth/auth.guard';
import { PagenotFoundComponent } from './common/pagenot-found/pagenot-found.component';
import { MainComponent } from '@app/public-layout/main/main.component';
import { guestGaurd } from './_guards/guest/guest.guard';
import { PermissionService } from './_services/permission.service';
import { inject } from '@angular/core';
import { ActionType, AppModule, EtfPages, InsurancePages, OCIOPages, OSMAPages } from './enums';

const moduleEligibility = (moduleName: string) => {
  const service = inject(PermissionService);
  const router = inject(Router);
  const status = service.hasAccess(moduleName) ? true : router.parseUrl('/app/home');
  return status;
};
const modulePageEligibility = (moduleName: string, pageName: string, actionString?: string) => {
  const service = inject(PermissionService);
  const router = inject(Router);
  const status = service.hasPageAccess(moduleName, pageName) ? true : false;
  let viewStatus = true;
  if (actionString) {
    const rights: string[] = service.getActionAccessList(moduleName, pageName) || [];
    viewStatus = service.hasActionPermission(rights, actionString);
  }
  if (!viewStatus) {
    return router.parseUrl('/unauthorized');
  }
  if (!status) {
    return router.parseUrl('404');
  }
  return status && viewStatus;
};

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'prefix',
    component: MainComponent,
    children: [],
  },
  {
    path: 'home',
    pathMatch: 'prefix',
    component: PublicLayoutComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@app/public-layout/home/home.component').then((m) => m.HomeComponent),
      },
    ],
  },
  {
    path: 'auth',
    component: BlankLayoutComponent,
    canActivate: [guestGaurd],
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('@app/auth/login/login.component').then((m) => m.LoginComponent),
      },
      {
        path: 'register',
        loadComponent: () =>
          import('@app/auth/register/register.component').then((m) => m.RegisterComponent),
      },
      {
        path: 'forget-password',
        loadComponent: () =>
          import('@app/auth/forget-password/forgetpassword.component').then(
            (m) => m.ForgetPasswordComponent
          ),
      },
      {
        path: 'terms-and-conditions',
        loadComponent: () =>
          import('@app/auth/terms-and-conditions/terms-and-conditions.component').then(
            (m) => m.TermsAndConditionsComponent
          ),
      },
    ],
  },
  {
    path: 'app',
    component: PostAuthLayoutComponent,
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'home', pathMatch: 'full' },
      {
        path: 'home',
        component: HomeLayoutComponent,
        canActivate: [authGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@app/layouts/post-auth-layout/home/home.component').then(
                (m) => m.HomeComponent
              ),
          },
          {
            path: 'profile',
            loadComponent: () =>
              import('@app/layouts/post-auth-layout/profile/user-information.component').then(
                (m) => m.UserInformationComponent
              ),
          },
        ],
      },
      {
        path: 'etf',
        component: MainlayoutComponent,
        canActivate: [() => moduleEligibility(AppModule.ETF)],
        children: [
          {
            path: 'dashboard',
            canActivate: [() => modulePageEligibility(AppModule.ETF, EtfPages.DASHBOARD)],
            loadComponent: () =>
              import('@app/post-auth/etf-pages/dashboard/dashboard.component').then(
                (m) => m.DashboardComponent
              ),
          },
          {
            path: 'custom-report',
            //  canActivate: [() => modulePageEligibility(AppModule.ETF', EtfPages.CUSTOM_REPORT)],
            loadComponent: () =>
              import('@app/post-auth/etf-pages/custom-report/custom-report.component').then(
                (m) => m.CustomReportComponent
              ),
          },
          {
            path: 'explorer',
            canActivate: [() => modulePageEligibility(AppModule.ETF, EtfPages.EXPLORER)],
            loadComponent: () =>
              import('@app/post-auth/etf-pages/explorer/etf-explorer.component').then(
                (m) => m.EtfExplorerComponent
              ),
          },
          {
            path: 'instrument',
            canActivate: [() => modulePageEligibility(AppModule.ETF, EtfPages.INSTRUMENT)],
            loadComponent: () =>
              import('@app/post-auth/etf-pages/instrument/instrument.component').then(
                (m) => m.InstrumentComponent
              ),
          },
          {
            path: 'instrument/details/:id',
            canActivate: [
              () => modulePageEligibility(AppModule.ETF, EtfPages.INSTRUMENT, ActionType.VIEW),
            ],
            loadComponent: () =>
              import('@app/post-auth/etf-pages/instrument/Instrument-details/instrument-details.component').then(
                (m) => m.InstrumentDetailsComponent
              ),
          },
          {
            path: 'instrument/add-instrument',
            canActivate: [
              () => modulePageEligibility(AppModule.ETF, EtfPages.INSTRUMENT, ActionType.NEW),
            ],
            loadComponent: () =>
              import('@app/post-auth/etf-pages/instrument/edit-instrument-details/edit-instrument-details.component').then(
                (m) => m.EditInstrumentDetailsComponent
              ),
          },
          {
            path: 'instrument/instrument-e/:id',
            canActivate: [
              () => modulePageEligibility(AppModule.ETF, EtfPages.INSTRUMENT, ActionType.EDIT),
            ],
            loadComponent: () =>
              import('@app/post-auth/etf-pages/instrument/edit-instrument-details/edit-instrument-details.component').then(
                (m) => m.EditInstrumentDetailsComponent
              ),
          },
          {
            path: 'entity',
            canActivate: [() => modulePageEligibility(AppModule.ETF, EtfPages.ENTITY)],
            loadComponent: () =>
              import('@app/post-auth/etf-pages/entity/entity.component').then(
                (m) => m.EntityComponent
              ),
          },
          {
            path: 'entity/info/:id',
            canActivate: [
              () => modulePageEligibility(AppModule.ETF, EtfPages.ENTITY, ActionType.VIEW),
            ],
            loadComponent: () =>
              import('@app/post-auth/etf-pages/entity/entity-details/entity-details.component').then(
                (m) => m.EntityDetailsComponent
              ),
          },
          {
            path: 'portfolio',
            canActivate: [() => modulePageEligibility(AppModule.ETF, EtfPages.PORTFOLIO)],
            loadComponent: () =>
              import('@app/post-auth/etf-pages/portfolio/portfolio.component').then(
                (m) => m.PortfolioComponent
              ),
          },
          {
            path: 'portfolio/add-portfolio',
            canActivate: [
              () => modulePageEligibility(AppModule.ETF, EtfPages.PORTFOLIO, ActionType.NEW),
            ],
            loadComponent: () =>
              import('@app/post-auth/etf-pages/portfolio/edit-portfolio-details/edit-portfolio-details.component').then(
                (m) => m.EditPortfolioDetailsComponent
              ),
          },
          {
            path: 'portfolio/details/:id',
            canActivate: [
              () => modulePageEligibility(AppModule.ETF, EtfPages.PORTFOLIO, ActionType.VIEW),
            ],
            loadComponent: () =>
              import('@app/post-auth/etf-pages/portfolio/portfolio-details/portfolio-details.component').then(
                (m) => m.PortfolioDetailsComponent
              ),
          },
          {
            path: 'portfolio/portfolio-e/:id',
            canActivate: [
              () => modulePageEligibility(AppModule.ETF, EtfPages.PORTFOLIO, ActionType.EDIT),
            ],
            loadComponent: () =>
              import('@app/post-auth/etf-pages/portfolio/edit-portfolio-details/edit-portfolio-details.component').then(
                (m) => m.EditPortfolioDetailsComponent
              ),
          },
          {
            path: 'ts',
            canActivate: [() => modulePageEligibility(AppModule.ETF, EtfPages.TS)],
            loadComponent: () =>
              import('@app/post-auth/etf-pages/ts/etf-ts.component').then((m) => m.EtfTsComponent),
          },
          {
            path: 'data-management',
            canActivate: [() => modulePageEligibility(AppModule.ETF, EtfPages.DATA_MANAGEMENT)],
            loadComponent: () =>
              import('@app/post-auth/etf-pages/data-management/data-management.component').then(
                (m) => m.DataManagementComponent
              ),
          },
          {
            path: 'admin',
            canActivate: [() => modulePageEligibility(AppModule.ETF, EtfPages.ADMIN)],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/admin.component').then(
                (m) => m.AdminComponent
              ),
          },
          {
            path: 'admin/add-user',
            // canActivate: [
            //   () => modulePageEligibility(AppModule.ETF, EtfPages.ADMIN, ActionType.NEW),
            // ],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/users/add-user/add-user.component').then(
                (m) => m.AddUserComponent
              ),
          },
          {
            path: 'admin/edit-user/:id',
            // canActivate: [
            //   () => modulePageEligibility(AppModule.ETF, EtfPages.ADMIN, ActionType.EDIT),
            // ],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/users/add-user/add-user.component').then(
                (m) => m.AddUserComponent
              ),
          },
          {
            path: 'admin/user-info/:user_id',
            canActivate: [
              () => modulePageEligibility(AppModule.ETF, EtfPages.ADMIN, ActionType.VIEW),
            ],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/users/userinfo/userinfo.component').then(
                (m) => m.UserinfoComponent
              ),
          },
          {
            path: 'admin/roles/roles-details/:id',
            canActivate: [() => modulePageEligibility(AppModule.ETF, EtfPages.ADMIN)],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/role-details/role-details.component').then(
                (m) => m.RoleDetailsComponent
              ),
          },
          {
            path: 'admin/authorties/authority-details/:id',
            canActivate: [() => modulePageEligibility(AppModule.INSURANCE, EtfPages.ADMIN)],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/authority-details/authority-details.component').then(
                (m) => m.AuthorityDetailsComponent
              ),
          },
          {
            path: 'admin/authorties/authority-e/:id',
            canActivate: [() => modulePageEligibility(AppModule.INSURANCE, EtfPages.ADMIN)],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/edit-authority-details/edit-authority-details.component').then(
                (m) => m.EditAuthorityDetailsComponent
              ),
          },
          {
            path: 'reports',
            canActivate: [() => modulePageEligibility(AppModule.ETF, EtfPages.REPORT)],
            loadComponent: () =>
              import('@app/post-auth/etf-pages/etf-report/etf-report.component').then(
                (m) => m.EtfReportComponent
              ),
          },
          {
            path: 'trade',
            canActivate: [() => modulePageEligibility(AppModule.ETF, 'trade')],
            loadComponent: () =>
              import('@app/post-auth/common-components/trade/trade.component').then(
                (m) => m.TradeComponent
              ),
          },
        ],
      },
      {
        path: 'insurance',
        component: MainlayoutComponent,
        canActivate: [() => moduleEligibility(AppModule.INSURANCE)],
        children: [
          {
            path: 'dashboard',
            canActivate: [
              () => modulePageEligibility(AppModule.INSURANCE, InsurancePages.DASHBOARD),
            ],
            loadComponent: () =>
              import('@app/post-auth/insurance-pages/dashboard/dashboard.component').then(
                (m) => m.DashboardComponent
              ),
          },
          {
            path: 'liabilities',
            canActivate: [
              () => modulePageEligibility(AppModule.INSURANCE, InsurancePages.LIABILITIES),
            ],
            loadComponent: () =>
              import('@app/post-auth/insurance-pages/liablities/liablities.component').then(
                (m) => m.LiablitiesComponent
              ),
          },
          {
            path: 'assets',
            canActivate: [() => modulePageEligibility(AppModule.INSURANCE, InsurancePages.ASSETS)],
            loadComponent: () =>
              import('@app/post-auth/insurance-pages/assets/assets.component').then(
                (m) => m.AssetsComponent
              ),
          },
          {
            path: 'trade',
            canActivate: [() => modulePageEligibility(AppModule.INSURANCE, InsurancePages.TRADE)],
            loadComponent: () =>
              import('@app/post-auth/common-components/trade/trade.component').then(
                (m) => m.TradeComponent
              ),
          },
          {
            path: 'reports',
            canActivate: [() => modulePageEligibility(AppModule.INSURANCE, InsurancePages.REPORT)],
            loadComponent: () =>
              import('@app/post-auth/insurance-pages/reports/insurance-reports.component').then(
                (m) => m.InsuranceReportsComponent
              ),
          },
          {
            path: 'explorer',
            canActivate: [
              () => modulePageEligibility(AppModule.INSURANCE, InsurancePages.EXPLORER),
            ],
            loadComponent: () =>
              import('@app/post-auth/insurance-pages/explorer/insurance-explorer.component').then(
                (m) => m.InsuranceExplorerComponent
              ),
          },
          {
            path: 'ts',
            canActivate: [() => modulePageEligibility(AppModule.INSURANCE, InsurancePages.TS)],
            loadComponent: () =>
              import('@app/post-auth/insurance-pages/ts/insurance-ts.component').then(
                (m) => m.InsuranceTsComponent
              ),
          },
          {
            path: 'users/info/:user_id',
            canActivate: [
              () =>
                modulePageEligibility(AppModule.INSURANCE, InsurancePages.USERS, ActionType.VIEW),
            ],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/users/userinfo/userinfo.component').then(
                (m) => m.UserinfoComponent
              ),
          },
          {
            path: 'data-management',
            canActivate: [
              () => modulePageEligibility(AppModule.INSURANCE, InsurancePages.DATA_MANAGEMENT),
            ],
            loadComponent: () =>
              import('@app/post-auth/insurance-pages/data-management/data-management.component').then(
                (m) => m.DataManagementComponent
              ),
          },
          {
            path: 'admin',
            canActivate: [() => modulePageEligibility(AppModule.INSURANCE, InsurancePages.ADMIN)],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/admin.component').then(
                (m) => m.AdminComponent
              ),
          },
          {
            path: 'admin/add-user',
            // canActivate: [
            //   () => modulePageEligibility(AppModule.INSURANCE, InsurancePages.ADMIN, ActionType.NEW),
            // ],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/users/add-user/add-user.component').then(
                (m) => m.AddUserComponent
              ),
          },
          {
            path: 'admin/edit-user/:id',
            // canActivate: [
            //   () => modulePageEligibility(AppModule.INSURANCE, InsurancePages.ADMIN, ActionType.EDIT),
            // ],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/users/add-user/add-user.component').then(
                (m) => m.AddUserComponent
              ),
          },
          {
            path: 'admin/roles/roles-details/:id',
            canActivate: [() => modulePageEligibility(AppModule.INSURANCE, InsurancePages.ADMIN)],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/role-details/role-details.component').then(
                (m) => m.RoleDetailsComponent
              ),
          },
          {
            path: 'admin/authorties/authority-details/:id',
            canActivate: [() => modulePageEligibility(AppModule.INSURANCE, InsurancePages.ADMIN)],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/authority-details/authority-details.component').then(
                (m) => m.AuthorityDetailsComponent
              ),
          },
          {
            path: 'admin/authorties/authority-e/:id',
            canActivate: [() => modulePageEligibility(AppModule.INSURANCE, InsurancePages.ADMIN)],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/edit-authority-details/edit-authority-details.component').then(
                (m) => m.EditAuthorityDetailsComponent
              ),
          },
          {
            path: 'admin/user-info/:user_id',
            canActivate: [
              () =>
                modulePageEligibility(AppModule.INSURANCE, InsurancePages.ADMIN, ActionType.VIEW),
            ],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/users/userinfo/userinfo.component').then(
                (m) => m.UserinfoComponent
              ),
          },
        ],
      },
      {
        path: 'ocio',
        component: MainlayoutComponent,
        canActivate: [() => moduleEligibility(AppModule.OCIO)],
        children: [
          {
            path: 'dashboard',
            canActivate: [() => modulePageEligibility(AppModule.OCIO, OCIOPages.DASHBOARD)],
            loadComponent: () =>
              import('@app/post-auth/ocio-pages/dashboard/dashboard.component').then(
                (m) => m.DashoardComponent
              ),
          },
          {
            path: 'explorer',
            canActivate: [() => modulePageEligibility(AppModule.OCIO, OCIOPages.EXPLORER)],
            loadComponent: () =>
              import('@app/post-auth/ocio-pages/explorer/ocio-explorer.component').then(
                (m) => m.OcioExplorerComponent
              ),
          },

          {
            path: 'data-management',
            canActivate: [() => modulePageEligibility(AppModule.OCIO, OCIOPages.DATA_MANAGEMENT)],
            loadComponent: () =>
              import('@app/post-auth/ocio-pages/data-management/data-management.component').then(
                (m) => m.DataManagementComponent
              ),
          },
          {
            path: 'documents',
            canActivate: [() => modulePageEligibility(AppModule.OCIO, OCIOPages.DOCUMENT)],
            loadComponent: () =>
              import('@app/post-auth/ocio-pages/documents/documents.component').then(
                (m) => m.DocumentsComponent
              ),
          },
          {
            path: 'admin',
            canActivate: [() => modulePageEligibility(AppModule.OCIO, OCIOPages.ADMIN)],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/admin.component').then(
                (m) => m.AdminComponent
              ),
          },
          {
            path: 'admin/add-user',
            // canActivate: [
            //   () => modulePageEligibility(AppModule.OCIO, OCIOPages.ADMIN, ActionType.NEW),
            // ],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/users/add-user/add-user.component').then(
                (m) => m.AddUserComponent
              ),
          },
          {
            path: 'admin/edit-user/:id',
            // canActivate: [
            //   () => modulePageEligibility(AppModule.OCIO, OCIOPages.ADMIN, ActionType.EDIT),
            // ],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/users/add-user/add-user.component').then(
                (m) => m.AddUserComponent
              ),
          },
          {
            path: 'admin/user-info/:user_id',
            canActivate: [
              () => modulePageEligibility(AppModule.OCIO, OCIOPages.ADMIN, ActionType.VIEW),
            ],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/users/userinfo/userinfo.component').then(
                (m) => m.UserinfoComponent
              ),
          },
          {
            path: 'admin/roles/roles-details/:id',
            canActivate: [() => modulePageEligibility(AppModule.OCIO, OCIOPages.ADMIN)],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/role-details/role-details.component').then(
                (m) => m.RoleDetailsComponent
              ),
          },
          {
            path: 'admin/authorties/authority-details/:id',
            canActivate: [() => modulePageEligibility(AppModule.OCIO, OCIOPages.ADMIN)],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/authority-details/authority-details.component').then(
                (m) => m.AuthorityDetailsComponent
              ),
          },
          {
            path: 'admin/authorties/authority-e/:id',
            canActivate: [() => modulePageEligibility(AppModule.OCIO, OCIOPages.ADMIN)],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/edit-authority-details/edit-authority-details.component').then(
                (m) => m.EditAuthorityDetailsComponent
              ),
          },
        ],
      },
      {
        path: 'osma',
        component: MainlayoutComponent,
        canActivate: [() => moduleEligibility(AppModule.OSMA)],
        children: [
          {
            path: 'dashboard',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OCIOPages.DASHBOARD)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/dashboard/dashboard.component').then(
                (m) => m.DashoardComponent
              ),
          },
          {
            path: 'risk-screener',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.RISK_SCREENER)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/risk-screener/risk-screener.component').then(
                (m) => m.RiskScreenerComponent
              ),
          },
          {
            path: 'proposal-screener',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.PROPOSAL_SCREENER)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/proposal-screener/proposal-screener.component').then(
                (m) => m.ProposalScreenerComponent
              ),
          },
          {
            path: 'reports',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.REPORT)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/osma-report/osma-report.component').then(
                (m) => m.OsmaReportComponent
              ),
          },
          {
            path: 'ts',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.TS)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/ts/osma-ts.component').then(
                (m) => m.OsmaTsComponent
              ),
          },
          {
            path: 'data-management',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.DATA_MANAGEMENT)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/data-management/data-management.component').then(
                (m) => m.DataManagementComponent
              ),
          },
          {
            path: 'admin',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.ADMIN)],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/admin.component').then(
                (m) => m.AdminComponent
              ),
          },
          {
            path: 'admin/add-user',
            // canActivate: [
            //   () => modulePageEligibility(AppModule.OSMA, OSMAPages.ADMIN, ActionType.NEW),
            // ],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/users/add-user/add-user.component').then(
                (m) => m.AddUserComponent
              ),
          },
          {
            path: 'admin/edit-user/:id',
            // canActivate: [
            //   () => modulePageEligibility(AppModule.OSMA, OSMAPages.ADMIN, ActionType.EDIT),
            // ],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/users/add-user/add-user.component').then(
                (m) => m.AddUserComponent
              ),
          },
          {
            path: 'advisor',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.ADVISOR)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/advisor/advisor.component').then(
                (m) => m.AdvisorComponent
              ),
          },
          {
            path: 'advisor/advisor-details/:id',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.ADVISOR)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/advisor/advisor-details/advisor-details.component').then(
                (m) => m.AdvisorDetailsComponent
              ),
          },
          {
            path: 'advisor/add-advisor',
            canActivate: [
              () => modulePageEligibility(AppModule.OSMA, OSMAPages.ADVISOR, ActionType.NEW),
            ],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/advisor/edit-advisor-details/edit-advisor-details.component').then(
                (m) => m.EditAdvisorDetailsComponent
              ),
          },
          {
            path: 'advisor/advisor-e/:id',
            canActivate: [
              () => modulePageEligibility(AppModule.OSMA, OSMAPages.ADVISOR, ActionType.EDIT),
            ],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/advisor/edit-advisor-details/edit-advisor-details.component').then(
                (m) => m.EditAdvisorDetailsComponent
              ),
          },
          {
            path: 'clients',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.CLIENTS)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/clients/clients.component').then(
                (m) => m.ClientsComponent
              ),
          },
          {
            path: 'clients/details/:id',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.CLIENTS)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/clients/client-details/client-details.component').then(
                (m) => m.ClientsDetailsComponent
              ),
          },
          {
            path: 'clients/add-client',
            canActivate: [
              () => modulePageEligibility(AppModule.OSMA, OSMAPages.CLIENTS, ActionType.NEW),
            ],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/clients/edit-client-details/edit-client-details.component').then(
                (m) => m.EditClientsDetailsComponent
              ),
          },
          {
            path: 'clients/client-e/:id',
            canActivate: [
              () => modulePageEligibility(AppModule.OSMA, OSMAPages.CLIENTS, ActionType.EDIT),
            ],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/clients/edit-client-details/edit-client-details.component').then(
                (m) => m.EditClientsDetailsComponent
              ),
          },
          {
            path: 'account',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.ACCOUNT)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/account/account.component').then(
                (m) => m.AccountComponent
              ),
          },
          {
            path: 'account/details/:id',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.ACCOUNT)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/account/account-details/account-details.component').then(
                (m) => m.AccountDetailsComponent
              ),
          },
          {
            path: 'account/add-account',
            canActivate: [
              () => modulePageEligibility(AppModule.OSMA, OSMAPages.ACCOUNT, ActionType.NEW),
            ],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/account/edit-account-details/edit-account-details.component').then(
                (m) => m.EditAccountDetailsComponent
              ),
          },
          {
            path: 'account/account-e/:id',
            canActivate: [
              () => modulePageEligibility(AppModule.OSMA, OSMAPages.ACCOUNT, ActionType.EDIT),
            ],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/account/edit-account-details/edit-account-details.component').then(
                (m) => m.EditAccountDetailsComponent
              ),
          },
          {
            path: 'alerts',
            // canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.ALERTS)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/alerts/alerts.component').then(
                (m) => m.AlertsComponent
              ),
          },
          {
            path: 'account-intake',
            // canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.ACCOUNT_INTAKE)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/account-intake/account-intake.component').then(
                (m) => m.AccountIntakeComponent
              ),
          },
          {
            path: 'custodian',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.CUSTODIAN)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/custodian/custodian.component').then(
                (m) => m.CustodianComponent
              ),
          },
          {
            path: 'custodian/details/:id',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.CUSTODIAN)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/custodian/custodian-details/custodian-details.component').then(
                (m) => m.CustodianDetailsComponent
              ),
          },
          {
            path: 'custodian/add-custodian',
            canActivate: [
              () => modulePageEligibility(AppModule.OSMA, OSMAPages.CUSTODIAN, ActionType.NEW),
            ],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/custodian/edit-custodian-details/edit-custodian-details.component').then(
                (m) => m.EditCustodianDetailsComponent
              ),
          },
          {
            path: 'custodian/custodian-e/:id',
            canActivate: [
              () => modulePageEligibility(AppModule.OSMA, OSMAPages.CUSTODIAN, ActionType.EDIT),
            ],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/custodian/edit-custodian-details/edit-custodian-details.component').then(
                (m) => m.EditCustodianDetailsComponent
              ),
          },

          {
            path: 'strategies',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.STRATEGIES)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/strategies/strategies.component').then(
                (m) => m.StrategiesComponent
              ),
          },
          {
            path: 'strategies/strategy-e/:id',
            canActivate: [
              () => modulePageEligibility(AppModule.OSMA, OSMAPages.STRATEGIES, ActionType.EDIT),
            ],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/strategies/edit-strategy-details/edit-strategy-details.component').then(
                (m) => m.EditStrategyDetailsComponent
              ),
          },
          {
            path: 'strategies/add-strategy',
            canActivate: [
              () => modulePageEligibility(AppModule.OSMA, OSMAPages.STRATEGIES, ActionType.NEW),
            ],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/strategies/edit-strategy-details/edit-strategy-details.component').then(
                (m) => m.EditStrategyDetailsComponent
              ),
          },

          {
            path: 'strategies/:id',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.STRATEGIES)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/strategies/strategy-details/strategy-details.component').then(
                (m) => m.StrategyDetailsComponent
              ),
          },
          {
            path: 'admin/user-info/:user_id',
            canActivate: [
              () => modulePageEligibility(AppModule.OSMA, OSMAPages.ADMIN, ActionType.VIEW),
            ],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/users/userinfo/userinfo.component').then(
                (m) => m.UserinfoComponent
              ),
          },
          {
            path: 'admin/roles/roles-details/:id',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.ADMIN)],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/role-details/role-details.component').then(
                (m) => m.RoleDetailsComponent
              ),
          },
          {
            path: 'admin/authorties/authority-details/:id',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.ADMIN)],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/authority-details/authority-details.component').then(
                (m) => m.AuthorityDetailsComponent
              ),
          },
          {
            path: 'admin/authorties/authority-e/:id',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.ADMIN)],
            loadComponent: () =>
              import('@app/post-auth/common-components/admin/edit-authority-details/edit-authority-details.component').then(
                (m) => m.EditAuthorityDetailsComponent
              ),
          },

          {
            path: 'documents',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.DOCUMENT)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/documents/documents.component').then(
                (m) => m.DocumentsComponent
              ),
          },

          {
            path: 'explorer',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.EXPLORER)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/explorer/osma-explorer.component').then(
                (m) => m.OsmaExplorerComponent
              ),
          },
          {
            path: 'advisor-dashbord',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.ADVISOR_DASHBOARD)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/advisor-dashboard/advisor-dashboard.component').then(
                (m) => m.AdvisorDashboardComponent
              ),
          },

          {
            path: 'client-dashboard',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.CLIENT_DASHBOARD)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/client-dashboard/client-dashboard.component').then(
                (m) => m.ClientDashboardComponent
              ),
          },
          {
            path: 'pm-dashboard',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.PM_DASHBOARD)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/pm-dashboard/pm-dashboard.component').then(
                (m) => m.PMDashboardComponent
              ),
          },
          {
            path: 'asset',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.ASSET)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/asset/asset.component').then(
                (m) => m.AssetComponent
              ),
          },
          {
            path: 'asset/details/:id',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.ASSET)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/asset/asset-details/asset-details.component').then(
                (m) => m.AssetDetailsComponent
              ),
          },
          {
            path: 'asset-dashboard',
            canActivate: [() => modulePageEligibility(AppModule.OSMA, OSMAPages.ASSET_DASHBOARD)],
            loadComponent: () =>
              import('@app/post-auth/osma-pages/asset-dashboard/asset-dashboard.component').then(
                (m) => m.AssetDashboardComponent
              ),
          },
          {
            path: 'matrix-core',
            loadComponent: () =>
              import('@app/post-auth/osma-pages/matrix-core/matrix-core.component').then(
                (m) => m.MatrixCoreComponent
              ),
          },
        ],
      },
    ],
  },
  { path: '404', component: PagenotFoundComponent },
  { path: '**', redirectTo: '404' },
];
